import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {UserLink} from 'src/components/links';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {getUserLabel} from 'src/utils/getUserLabel';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import Button from '@pb/kit-component-button';
import {
    Drawer,
    DrawerFooter,
    DrawerHeader,
    DrawerHole,
    DrawerMain,
} from '@pb/kit-component-drawer';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Pill, StripOfPills} from '@pb/kit-component-strip-of-pills';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';


import type {DetailedLogHits} from 'src/openapi/admin/models/DetailedLogHits';
import type {Error} from 'src/openapi/admin/models/Error';
import type {ServerResponse} from 'src/typedef';

type Props = {
    selectedLogId: string | null,
    open: boolean,
    onClose: () => void,
    loader: (logId: string) => Promise<{
        type: string,
        payload: {
            data: DetailedLogHits,
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
};

export const ChangeLogDrawerContent = ({onClose, selectedLogId, open, loader}: Props) => {
    const [detailsResponse, setDetailsResponse] = useState<ServerResponse<DetailedLogHits> | null>(null);
    const {locale} = useContext(LocaleContext);
    const tabsRef = useRef<HTMLElement | null>(null);

    const loadDetails = useCallback(async (logId: string) => {
        setDetailsResponse(null);
        const response = await loader(logId);

        if (response.payload.success) {
            setDetailsResponse({data: response.payload.data});
        } else {
            setDetailsResponse({error: response.payload.data});
        }
    }, [loader]);

    useEffect(() => {
        tabsRef.current = document.querySelector('.tabs');
    }, []);

    useEffect(() => {
        if (selectedLogId) {
            loadDetails(selectedLogId);
        }
    }, [selectedLogId, loadDetails]);

    const title = useMemo(() => {
        return (
            <span className="enable-selection">
                {detailsResponse?.data?.actionName || ''}
            </span>
        );
    }, [detailsResponse]);

    const changeDetails = detailsResponse?.data?.changeInfo;

    const handleSetOpen = useCallback((shown: boolean) => {
        if (!shown) {
            onClose();
        }
    }, [onClose]);

    return (
        <Drawer
            open={open}
            setOpen={handleSetOpen}
            placement="right"
            stickToElement={tabsRef.current || undefined}
            style={{width: 'fit-content'}}>
            <DrawerHeader>
                {title}
            </DrawerHeader>
            <DrawerMain>
                <DrawerHole>
                    <div style={{width: 700}} className="enable-selection">
                        <Skeleton shown={!detailsResponse} view={<div/>}>
                            <ServerResponseHandler serverError={detailsResponse?.error || null} smallView={true}>
                                <Group direction="v" alignItems="start">
                                    <Group direction="v" alignItems="start" gap="xs">
                                        <Label>Action Type</Label>
                                        <div>{detailsResponse?.data?.actionName || '--'}</div>
                                    </Group>
                                    <Group direction="v" alignItems="start" gap="xs">
                                        <Label>Action Description</Label>
                                        <p>{detailsResponse?.data?.actionDescription || '--'}</p>
                                    </Group>
                                    <Group direction="v" alignItems="start" gap="xs">
                                        <Label>Entity</Label>
                                        <StripOfPills>
                                            {detailsResponse?.data?.labels ? (
                                                Object.keys(detailsResponse?.data?.labels).map(label => (
                                                    <Pill key={label} name={label}/>
                                                ))
                                            ) : '--'}
                                        </StripOfPills>
                                    </Group>
                                    <Group direction="v" alignItems="start" gap="xs">
                                        <Label>Initiator System</Label>
                                        <div>{detailsResponse?.data?.initiatorSystem || '--'}</div>
                                    </Group>
                                    <Group direction="v" alignItems="start" gap="xs">
                                        <Label>Initiator System Process</Label>
                                        <div>{detailsResponse?.data?.initiatorSystemProcess || '--'}</div>
                                    </Group>
                                    {detailsResponse?.data?.initiatorUser ? (
                                        <Group direction="v" alignItems="start" gap="xs">
                                            <Label>Initiator</Label>
                                            <UserLink
                                                place="Change Log Drawer"
                                                caption={getUserLabel(detailsResponse.data.initiatorUser)}
                                                userId={detailsResponse.data.initiatorUser.userId}
                                            />
                                        </Group>
                                    ) : null}
                                    <Group direction="v" alignItems="start" gap="xs">
                                        <Label>Timestamp</Label>
                                        <div>{FormattedDate.format({
                                            value: detailsResponse?.data?.timestamp,
                                            format: mapDateFormat(locale.dateFormat, 'WORD', 'DATE_TIME')}) || '--'}
                                        </div>
                                    </Group>
                                    <Group direction="v" alignItems="start" gap="xs">
                                        <Label>Reason</Label>
                                        <div>{detailsResponse?.data?.reason || '--'}</div>
                                    </Group>
                                    <Group direction="v" alignItems="start" gap="xs">
                                        <Label>Details</Label>
                                        {(changeDetails?.length && changeDetails.length > 0) ? (
                                            <Table borderedGrid={true}>
                                                <Head>
                                                    <Cell>Field</Cell>
                                                    <Cell>Old Value</Cell>
                                                    <Cell>New Value</Cell>
                                                </Head>
                                                <Body>
                                                    {detailsResponse?.data?.changeInfo.map((changeInfo, index) => (
                                                        <Row key={index}>
                                                            <Cell>
                                                                <EllipsisTextHint title={changeInfo.fieldName}>
                                                                    {changeInfo.fieldName}
                                                                </EllipsisTextHint>
                                                            </Cell>
                                                            <Cell>
                                                                <EllipsisTextHint title={changeInfo.oldValue}>
                                                                    {changeInfo.oldValue || '--'}
                                                                </EllipsisTextHint>
                                                            </Cell>
                                                            <Cell>
                                                                <EllipsisTextHint title={changeInfo.newValue}>
                                                                    {changeInfo.newValue || '--'}
                                                                </EllipsisTextHint>
                                                            </Cell>
                                                        </Row>
                                                    ))}
                                                </Body>
                                            </Table>
                                        ) : (
                                            <div>
                                                No attribute changes were detected. This log entry may have been
                                                triggered by an action that did not modify any data.
                                            </div>
                                        )}
                                    </Group>
                                </Group>
                            </ServerResponseHandler>
                        </Skeleton>
                    </div>
                </DrawerHole>
            </DrawerMain>
            <DrawerFooter>
                <Button variant="Type.PRIMARY" onClick={onClose}>
                    Close
                </Button>
            </DrawerFooter>
        </Drawer>
    );
};
